/**
 * Language Constants - Single Source of Truth
 *
 * Complete list of all 39 languages from the V2 schema LanguageCode enum.
 * All components should import from here instead of defining their own arrays.
 */
import { LanguageCode } from '@segmently/schema-models/v2';

export const Language = LanguageCode;
export type Language = LanguageCode;

export interface LanguageOption {
  code: Language;
  name: string;
}

/**
 * Complete list of all supported languages (39 total)
 * Organized by language groups for better UX in dropdowns
 */
export const LANGUAGES: LanguageOption[] = [
  // English variants
  { code: Language.EnUs, name: 'English (US)' },
  { code: Language.EnGb, name: 'English (UK)' },
  { code: Language.EnAu, name: 'English (Australia)' },
  { code: Language.EnCa, name: 'English (Canada)' },
  // Spanish variants
  { code: Language.EsEs, name: 'Spanish (Spain)' },
  { code: Language.EsMx, name: 'Spanish (Mexico)' },
  // French variants
  { code: Language.Fr, name: 'French' },
  { code: Language.FrCa, name: 'French (Canada)' },
  // Portuguese variants
  { code: Language.PtBr, name: 'Portuguese (Brazil)' },
  { code: Language.PtPt, name: 'Portuguese (Portugal)' },
  // Chinese variants
  { code: Language.ZhHans, name: 'Chinese (Simplified)' },
  { code: Language.ZhHant, name: 'Chinese (Traditional)' },
  // Other languages (alphabetical by English name)
  { code: Language.Ar, name: 'Arabic' },
  { code: Language.Ca, name: 'Catalan' },
  { code: Language.Hr, name: 'Croatian' },
  { code: Language.Cs, name: 'Czech' },
  { code: Language.Da, name: 'Danish' },
  { code: Language.Nl, name: 'Dutch' },
  { code: Language.Fi, name: 'Finnish' },
  { code: Language.De, name: 'German' },
  { code: Language.El, name: 'Greek' },
  { code: Language.He, name: 'Hebrew' },
  { code: Language.Hi, name: 'Hindi' },
  { code: Language.Hu, name: 'Hungarian' },
  { code: Language.Id, name: 'Indonesian' },
  { code: Language.It, name: 'Italian' },
  { code: Language.Ja, name: 'Japanese' },
  { code: Language.Ko, name: 'Korean' },
  { code: Language.Ms, name: 'Malay' },
  { code: Language.No, name: 'Norwegian' },
  { code: 'nb' as Language, name: 'Norwegian Bokmal (nb)' },
  { code: Language.Pl, name: 'Polish' },
  { code: Language.Ro, name: 'Romanian' },
  { code: Language.Ru, name: 'Russian' },
  { code: Language.Sk, name: 'Slovak' },
  { code: Language.Sv, name: 'Swedish' },
  { code: Language.Th, name: 'Thai' },
  { code: Language.Tr, name: 'Turkish' },
  { code: Language.Uk, name: 'Ukrainian' },
  { code: Language.Vi, name: 'Vietnamese' },
];

/**
 * Get language display name by code
 * @param code - Language code from Language enum
 * @returns Display name or the code itself if not found
 */
export function getLanguageName(code: Language): string {
  return LANGUAGES.find((l) => l.code === code)?.name || code;
}
